import { Component, OnInit, Inject } from '@angular/core';
import { SiteFormComponent } from 'src/app/shared/forms/site-form/site-form.component';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SiteService } from 'src/app/core/services/site.service';
import { AlertService } from 'src/app/core/services/alert.service';
import { environment } from '../../../environments/environment';
import { ImageService } from 'src/app/core/services/image.service';
import {
  ImageUploadComponent,
  UploadResponse,
} from 'src/app/shared/image-upload/image-upload.component';
import { Site } from '@core/interfaces/api';
import {
  getSiteLogoUrl,
  getSitePrintLogoUrl,
  getSiteImageUrl,
  getSiteMenuLogoUrl,
} from '@features/sites/utils/site.utils';
@Component({
  providers: [ImageUploadComponent],
  selector: 'app-edit-site',
  templateUrl: './edit-site.component.html',
  styleUrls: ['./edit-site.component.scss'],
})
export class EditSiteComponent implements OnInit {
  public siteForm = SiteFormComponent.formModel();
  public submitting = false;
  public env = environment;
  public logoUrl: string;
  public printLogoUrl: string;
  public menuLogoUrl: string;
  public logoSrc: string;
  public printLogoSrc: string;
  public menuLogoSrc: string;
  public imageUrl: string;
  public imageSrc: string;

  constructor(
    private alertService: AlertService,
    public imageService: ImageService,
    private imageUploadComponent: ImageUploadComponent,
    private siteService: SiteService,
    private dialogRef: MatDialogRef<EditSiteComponent>,
    @Inject(MAT_DIALOG_DATA) public site: Site,
  ) {}

  ngOnInit() {
    this.siteForm.setValue(SiteFormComponent.deserialize(this.site));
    this.getImageUrls();
  }

  public onSaveSiteDetails(): void {
    this.submitting = true;
    const updatedSite = SiteFormComponent.serialize(
      this.siteForm.value,
      this.site,
    );
    this.siteService
      .updateSite(updatedSite.id, updatedSite as unknown as Site)
      .then(() => {
        this.siteService.refreshSites();

        // If editing active site, refresh site data
        if (this.site.id == this.siteService.currentSiteId) {
          this.siteService.setSiteDetails(this.site.id);
        }

        this.alertService.success('Property saved.');
        this.closeDialog();
      })
      .catch((error) => {
        this.alertService.error('Unable to save property. Please try again.');
        console.error(error);
      })
      .finally(() => (this.submitting = false));
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  public editLogo(): void {
    const imageUploadDialogRef = this.imageUploadComponent.editSiteLogo(
      this.site.id,
    );
    imageUploadDialogRef.afterClosed().subscribe((result: UploadResponse) => {
      if (result.success) {
        this.logoSrc = `${this.logoUrl}&timestamp=${new Date().getTime()}`;
      }
    });
  }

  public editPrintLogo(): void {
    const printImageUploadDialogRef =
      this.imageUploadComponent.editSitePrintLogo(this.site.id);
    printImageUploadDialogRef
      .afterClosed()
      .subscribe((result: UploadResponse) => {
        if (result.success) {
          this.printLogoSrc = `${
            this.printLogoUrl
          }&timestamp=${new Date().getTime()}`;
        }
      });
  }

  public editMenuLogo(): void {
    const imageUploadDialogRef =
      this.imageUploadComponent.editMenuLogo(this.site.id);
    imageUploadDialogRef
      .afterClosed()
      .subscribe((result: UploadResponse) => {
        if (result.success) {
          this.menuLogoSrc = `${this.menuLogoUrl}&timestamp=${new Date().getTime()}`;
          // Save the Firebase Storage URL to the site's menuBugImageUrl field
          this.siteService
            .updateSite(this.site.id, { menuBugImageUrl: this.menuLogoUrl })
            .then(() => {
              this.site.menuBugImageUrl = this.menuLogoUrl;
              this.alertService.success('Menu logo saved.');
            })
            .catch(() => {
              this.alertService.error('Logo uploaded but failed to save URL. Please try again.');
            });
        }
      });
  }

  public editImage(): void {
    const imageUploadDialogRef = this.imageUploadComponent.editImage(
      this.site.id,
    );
    imageUploadDialogRef.afterClosed().subscribe((result: UploadResponse) => {
      if (result.success) {
        this.imageSrc = `${this.imageUrl}&timestamp=${new Date().getTime()}`;
      }
    });
  }

  private getImageUrls(): void {
    this.logoUrl = getSiteLogoUrl(this.env.storageUrl, this.site.id);
    this.printLogoUrl = getSitePrintLogoUrl(this.env.storageUrl, this.site.id);
    this.menuLogoUrl = getSiteMenuLogoUrl(this.env.storageUrl, this.site.id);
    this.imageUrl = getSiteImageUrl(this.env.storageUrl, this.site.id);

    // initialize image srcs
    this.logoSrc = `${this.logoUrl}&timestamp=${new Date().getTime()}`;
    this.printLogoSrc = `${
      this.printLogoUrl
    }&timestamp=${new Date().getTime()}`;
    this.menuLogoSrc = this.site.menuBugImageUrl
      ? `${this.site.menuBugImageUrl}&timestamp=${new Date().getTime()}`
      : `${this.menuLogoUrl}&timestamp=${new Date().getTime()}`;
    this.imageSrc = `${this.imageUrl}&timestamp=${new Date().getTime()}`;
  }

  removeLogo(type: 'print' | 'gigi' | 'portrait' | 'menu'): void {
    const url =
      type === 'print'
        ? this.printLogoUrl
        : type === 'portrait'
        ? this.imageUrl
        : type === 'menu'
        ? this.menuLogoUrl
        : this.logoUrl;
    const name =
      type === 'print'
        ? 'print logo'
        : type === 'portrait'
        ? 'portrait image'
        : type === 'menu'
        ? 'menu logo'
        : 'GiGi logo';

    this.imageService
      .deleteFromFirestore(url)
      .then(() => {
        this.alertService.success(`Successfully removed ${name}.`);
        if (type === 'print') {
          this.printLogoSrc = null;
        } else if (type === 'portrait') {
          this.imageSrc = null;
        } else if (type === 'menu') {
          this.menuLogoSrc = null;
          // Clear the menuBugImageUrl in the database
          this.siteService
            .updateSite(this.site.id, { menuBugImageUrl: null })
            .then(() => { this.site.menuBugImageUrl = null; })
            .catch(() => {
              this.menuLogoSrc = `${this.menuLogoUrl}&timestamp=${new Date().getTime()}`;
              this.alertService.error('Failed to clear menu logo URL. Please try again.');
            });
        } else {
          this.logoSrc = null;
        }
      })
      .catch(() => {
        this.alertService.error(`Unable to delete ${name}.`);
      });
  }
}
